import { useQueryClient } from "@tanstack/react-query";

import { useCurrentCompanyId, useCurrentJobId } from "@/hooks";
import { Candidacy, CandidacyWithProfileResponse } from "@/types";

/**
 * This hook is used to update a candidacy in the candidacies query without
 * refetching the entire query. Also updates time in stage and/or candidacy
 * interest if applicable.
 */
export const useSetCandidacyInCandidacies = () => {
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();
  const queryClient = useQueryClient();

  return ({
    candidacy,
    isInterested,
    timeInStage,
  }: {
    candidacy: Candidacy;
    isInterested?: boolean;
    timeInStage?: string;
  }) => {
    queryClient.setQueryData(
      ["candidacies", currentCompanyId, currentJobId],
      (cachedData: CandidacyWithProfileResponse[]) => {
        return cachedData.map(
          (candidacyWithProfileResponse: CandidacyWithProfileResponse) =>
            candidacyWithProfileResponse.candidacy.id === candidacy.id
              ? {
                  ...candidacyWithProfileResponse,
                  candidacy,
                  // Have to check for `undefined` because `isInterested` could be `false`!
                  ...(isInterested !== undefined && {
                    candidacyInterest: isInterested,
                  }),
                  ...(timeInStage && { timeInStage }),
                }
              : { ...candidacyWithProfileResponse },
        );
      },
    );
  };
};
